import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, GitCompare, Search } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { UniversalSearch } from "@/components/UniversalSearch";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "MBA", to: "/mba" },
  { label: "PGDM", to: "/pgdm" },
  { label: "Colleges", to: "/colleges" },
  { label: "Courses", to: "/courses" },
  { label: "Exams", to: "/exams" },
  { label: "Rankings", to: "/rankings" },
  { label: "News", to: "/news" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { items } = useCompare();
  const { student } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setShowSearch(false), [location.pathname]);

  return (
    <header data-testid="main-nav" className={`sticky top-0 z-50 transition-colors ${scrolled ? "glass border-b border-black/5" : "bg-white border-b border-black/5"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className="px-3 py-2 text-sm font-medium text-navy/80 hover:text-navy rounded-lg hover:bg-secondary transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSearch((s) => !s)} data-testid="header-search-toggle"
              className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center text-navy">
              <Search size={18} />
            </button>
            <Link to="/compare" data-testid="nav-compare" className="relative w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center text-navy">
              <GitCompare size={18} />
              {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-gold text-navy-dark text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{items.length}</span>}
            </Link>
            <Link to={student ? "/dashboard" : "/login"} data-testid="nav-login"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-navy hover:bg-secondary rounded-full">
              <User size={16} /> {student ? (student.name || "Account") : "Login"}
            </Link>
            <EnquiryDialog trigger={
              <button data-testid="primary-cta" className="hidden md:block gold-btn rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap">
                Get Recommendations
              </button>} />
            <Sheet>
              <SheetTrigger asChild>
                <button className="lg:hidden w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center text-navy" data-testid="mobile-menu-btn"><Menu size={20} /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="mt-6 flex flex-col gap-1">
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} className="px-3 py-3 rounded-lg hover:bg-secondary text-navy font-medium">{n.label}</Link>
                  ))}
                  <Link to={student ? "/dashboard" : "/login"} className="px-3 py-3 rounded-lg hover:bg-secondary text-navy font-medium flex items-center gap-2"><User size={16} /> {student ? "My Account" : "Login"}</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {showSearch && (
          <div className="pb-4 pt-1"><UniversalSearch /></div>
        )}
      </div>
    </header>
  );
}
